import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Stack, IconButton, Divider, Button } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';

const socialLinks = [
  { icon: InstagramIcon, url: 'https://www.instagram.com/highway.rangers/', label: 'Instagram' },
  { icon: FacebookIcon, url: 'https://www.facebook.com/profile.php?id=61571521665396', label: 'Facebook' },
  { icon: XIcon, url: 'https://x.com/HighwayRangers', label: 'X (Twitter)' },
  { icon: YouTubeIcon, url: 'https://www.youtube.com/@TexasHighwayRangers', label: 'YouTube' },
];

const footerLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'News', path: '/news' },
];

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#002244',
        color: '#fff',
        mt: 'auto',
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: 4, bgcolor: '#ffc107' }} />
      
      <Container maxWidth="lg" sx={{ pt: 6, pb: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          justifyContent="space-between"
          alignItems={{ xs: 'center', md: 'flex-start' }}
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {/* Brand */}
          <Box sx={{ maxWidth: 360 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Box
                component="img"
                src="/thr-logo-small.png"
                alt=""
                sx={{ height: 36, width: 'auto' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  letterSpacing: '-0.01em',
                }}
              >
                TEXAS HIGHWAY RANGERS
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.8, mb: 2 }}>
              A state-recognized nonprofit in Texas providing free emergency roadside assistance to motorists. 
              Powered entirely by volunteer dedication and community generosity.
            </Typography>
            <Button
              href="https://www.paypal.com/donate/?hosted_button_id=YOUR_BUTTON_ID"
              target="_blank"
              variant="contained"
              size="small"
              sx={{
                bgcolor: '#ffc107',
                color: '#003366',
                fontWeight: 700,
                '&:hover': {
                  bgcolor: '#ffca28',
                },
              }}
            >
              Support Our Mission
            </Button>
          </Box>

          {/* Quick Links */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontWeight: 700, 
                mb: 2, 
                display: 'block',
                color: '#ffc107',
                letterSpacing: '0.1em',
              }}
            >
              Quick Links
            </Typography>
            <Stack spacing={1}>
              {footerLinks.map((link) => (
                <Typography
                  key={link.path}
                  component={Link}
                  to={link.path}
                  variant="body2"
                  sx={{
                    opacity: 0.8,
                    transition: 'all 0.2s',
                    textDecoration: 'none',
                    color: '#fff',
                    '&:hover': { 
                      opacity: 1,
                      color: '#ffc107',
                    },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Stack>
          </Box>

          {/* Contact */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontWeight: 700, 
                mb: 2, 
                display: 'block',
                color: '#ffc107',
                letterSpacing: '0.1em',
              }}
            >
              Contact Us
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <EmailIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  howdy@highwayrangers.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <PlaceIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Dallas-Fort Worth & Austin, Texas
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Social */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontWeight: 700, 
                mb: 2, 
                display: 'block',
                color: '#ffc107',
                letterSpacing: '0.1em',
              }}
            >
              Follow Us
            </Typography>
            <Stack direction="row" spacing={1}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  size="small"
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: '#ffc107',
                      color: '#003366',
                    },
                  }}
                >
                  <social.icon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.15)' }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="body2" sx={{ opacity: 0.6, fontSize: '0.8rem' }}>
            © {new Date().getFullYear()} Texas Highway Rangers, Inc. All rights reserved. State-Recognized Nonprofit
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              opacity: 0.8, 
              fontStyle: 'italic',
              fontSize: '0.85rem',
            }}
          >
            Texans Helping Texans, One Mile at a Time.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

export default Footer;
